import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Activity, DollarSign, Clock, Settings, Download, Filter, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import apiService from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('clinical');
  const [dateRange, setDateRange] = useState('3months');
  const [selectedScoreType, setSelectedScoreType] = useState('all');
  const [selectedTherapists, setSelectedTherapists] = useState([]);
  const [selectedProgressType, setSelectedProgressType] = useState('all');
  const [fundingChartFilter, setFundingChartFilter] = useState('top5');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [realData, setRealData] = useState({
    clinicalOutcomes: null,
    providers: null,
    demographics: null,
    funding: null,
    engagement: null
  });

  // Define tabs array
  const tabs = [
    { id: 'clinical', label: 'Clinical Outcomes', icon: TrendingUp, component: ClinicalOutcomesReport },
    { id: 'providers', label: 'Provider Productivity', icon: Users, component: ProviderProductivityReport },
    { id: 'demographics', label: 'Demographics', icon: Activity, component: DemographicReport },
    { id: 'funding', label: 'Funding Sources', icon: DollarSign, component: FundingReport },
    { id: 'engagement', label: 'Engagement', icon: Clock, component: EngagementReport },
    { id: 'custom', label: 'Custom Reports', icon: Settings, component: CustomReportBuilder }
  ];

  // Load therapists on component mount
  useEffect(() => {
    const loadTherapists = async () => {
      try {
        const therapistsData = await apiService.getTherapists();
        setTherapists(therapistsData.data || []);
      } catch (err) {
        console.error('Error loading therapists:', err);
      }
    };
    
    loadTherapists();
  }, []);

  // Load real data when component mounts or filters change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Map frontend date range to backend format
        const mapDateRange = (range) => {
          switch (range) {
            case '3months': return 'last-quarter';
            case '6months': return 'last-quarter'; // Use quarter for 6 months as closest match
            case '1year': return 'last-year';
            default: return 'last-quarter';
          }
        };

        const params = { 
          dateRange: mapDateRange(dateRange),
          startDate: new Date(Date.now() - (dateRange === '3months' ? 90 : dateRange === '6months' ? 180 : 365) * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date().toISOString()
        };

        // Clear stale data for tabs that aren't currently active when date range changes
        if (activeTab !== 'clinical') {
          setRealData(prev => ({ ...prev, clinicalOutcomes: null }));
        }
        if (activeTab !== 'providers') {
          setRealData(prev => ({ ...prev, providers: null, sessionVolume: null }));
        }
        if (activeTab !== 'demographics') {
          setRealData(prev => ({ ...prev, demographics: null }));
        }
        if (activeTab !== 'funding') {
          setRealData(prev => ({ ...prev, funding: null, claims: null }));
        }
        if (activeTab !== 'engagement') {
          setRealData(prev => ({ ...prev, engagement: null }));
        }

        switch (activeTab) {
          case 'clinical':
            const clinicalData = await apiService.getClinicalOutcomes({ ...params, assessmentTool: selectedScoreType });
            setRealData(prev => ({ ...prev, clinicalOutcomes: clinicalData }));
            break;
          case 'providers':
            const providersData = await apiService.getProviderProductivity(params);
            const sessionVolumeData = await apiService.getSessionVolume(params);
            setRealData(prev => ({ 
              ...prev, 
              providers: providersData,
              sessionVolume: sessionVolumeData
            }));
            break;
          case 'demographics':
            const demographicsData = await apiService.getDemographics(params);
            setRealData(prev => ({ ...prev, demographics: demographicsData }));
            break;
          case 'funding':
            const fundingData = await apiService.getFundingSources(params);
            const claimsData = await apiService.getClaimsData(params);
            setRealData(prev => ({ 
              ...prev, 
              funding: fundingData,
              claims: claimsData
            }));
            break;
          case 'engagement':
            const engagementData = await apiService.getEngagementMetrics(params);
            // Note: attendanceData now comes from the same metrics endpoint
            setRealData(prev => ({ 
              ...prev, 
              engagement: engagementData
            }));
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activeTab, dateRange, selectedScoreType, selectedTherapists, selectedProgressType]);

  const FilterControls = () => (
    <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
      <div className="flex flex-wrap gap-4 items-center">
        {/* Date Range Filter - Always visible */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="border rounded px-3 py-1 text-sm"
          >
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">Last Year</option>
          </select>
        </div>
        
        {/* Therapist Multi-Select - Always visible */}
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <div className="relative">
            <select 
              value={selectedTherapists.length === 0 ? "" : selectedTherapists[0]}
              onChange={(e) => {
                if (e.target.value === "") {
                  setSelectedTherapists([]);
                } else {
                  const therapistId = e.target.value;
                  if (!selectedTherapists.includes(therapistId)) {
                    setSelectedTherapists([...selectedTherapists, therapistId]);
                  }
                }
              }}
              className="border rounded px-3 py-1 text-sm min-w-48"
            >
              <option value="">
                {selectedTherapists.length === 0 
                  ? "All Therapists" 
                  : `${selectedTherapists.length} Therapist${selectedTherapists.length > 1 ? 's' : ''} Selected`
                }
              </option>
              {therapists
                .filter(therapist => !selectedTherapists.includes(therapist._id))
                .map(therapist => (
                  <option key={therapist._id} value={therapist._id}>
                    {therapist.name} {therapist.licenseNumber ? `(${therapist.licenseNumber})` : ''}
                  </option>
                ))
              }
            </select>
            {selectedTherapists.length > 0 && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg p-2 z-10 min-w-48">
                <div className="text-xs text-gray-600 mb-2">Selected Therapists:</div>
                {selectedTherapists.map(therapistId => {
                  const therapist = therapists.find(t => t._id === therapistId);
                  return therapist ? (
                    <div key={therapistId} className="flex items-center justify-between py-1">
                      <span className="text-sm">{therapist.name}</span>
                      <button 
                        onClick={() => setSelectedTherapists(selectedTherapists.filter(id => id !== therapistId))}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  ) : null;
                })}
                <button 
                  onClick={() => setSelectedTherapists([])}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Assessment Tool Filter - Clinical tab only */}
        {activeTab === 'clinical' && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <select 
              value={selectedScoreType} 
              onChange={(e) => setSelectedScoreType(e.target.value)}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="all">All Assessment Tools</option>
              <option value="PHQ-9">PHQ-9</option>
              <option value="GAD-7">GAD-7</option>
              <option value="AUDIT">AUDIT</option>
              <option value="DAST">DAST</option>
              <option value="PCL-C">PCL-C</option>
              <option value="BTQ">BTQ</option>
            </select>
          </div>
        )}

        {/* Progress Type Filter - Clinical tab only */}
        {activeTab === 'clinical' && (
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            <select 
              value={selectedProgressType}
              onChange={(e) => setSelectedProgressType(e.target.value)}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="all">All Progress Types</option>
              <option value="improved">Improved Only</option>
              <option value="unchanged">Unchanged Only</option>
              <option value="worsened">Worsened Only</option>
            </select>
          </div>
        )}
        
        {/* PDF Export - Always visible */}
        <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">
          <Download className="w-4 h-4" />
          PDF
        </button>
      </div>
    </div>
  );

  function ClinicalOutcomesReport() {
    // Use real data from API
    const realOutcomes = realData.clinicalOutcomes?.data || [];
    const realTrajectory = realData.clinicalOutcomes?.trajectory || [];
    const realSummary = realData.clinicalOutcomes?.summary || { improved: 0, unchanged: 0, worsened: 0 };
    
    // Filter data by selected score type
    const filteredOutcomes = selectedScoreType === 'all' 
      ? realOutcomes 
      : realOutcomes.filter(item => item.formName === selectedScoreType);

    // Use real trajectory data from API
    const trajectoryData = realTrajectory;
    


    // Calculate individual client data for table
    const outcomeData = filteredOutcomes.map(item => ({
      ...item,
      improvement: item.score_PHQ_9 > 0 ? ((item.score_PHQ_9 - (item.score_PHQ_9 * 0.8)) / item.score_PHQ_9 * 100).toFixed(1) : 
                  item.score_GAD_7 > 0 ? ((item.score_GAD_7 - (item.score_GAD_7 * 0.8)) / item.score_GAD_7 * 100).toFixed(1) : 0
    }));

    // Use real summary stats from API
    const summaryStats = realSummary;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">{summaryStats.improved}%</div>
            <div className="text-sm text-green-600">Improved</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">{summaryStats.unchanged}%</div>
            <div className="text-sm text-yellow-600">Unchanged</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-700">{summaryStats.worsened}%</div>
            <div className="text-sm text-red-600">Worsened</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Clinical Outcome Trajectory</h3>
          <p className="text-sm text-gray-600 mb-4">
            Average scores for each assessment tool across all clients throughout treatment progression. Each line represents a different assessment form (lower scores indicate improvement).
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trajectoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timePoint" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="PHQ-9" 
                stroke="#8884d8" 
                strokeWidth={2} 
                name="PHQ-9 (Depression)"
                connectNulls={false}
                dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="GAD-7" 
                stroke="#82ca9d" 
                strokeWidth={2} 
                name="GAD-7 (Anxiety)"
                connectNulls={false}
                dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="AUDIT" 
                stroke="#ffc658" 
                strokeWidth={2} 
                name="AUDIT (Alcohol)"
                connectNulls={false}
                dot={{ fill: '#ffc658', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="DAST" 
                stroke="#ff7300" 
                strokeWidth={2} 
                name="DAST (Substance)"
                connectNulls={false}
                dot={{ fill: '#ff7300', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="PCL-C" 
                stroke="#8dd1e1" 
                strokeWidth={2} 
                name="PCL-C (PTSD)"
                connectNulls={false}
                dot={{ fill: '#8dd1e1', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="BTQ" 
                stroke="#d084d0" 
                strokeWidth={2} 
                name="BTQ (Trauma)"
                connectNulls={false}
                dot={{ fill: '#d084d0', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Individual Client Progress</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Client ID</th>
                  <th className="text-left p-2">Therapist</th>
                  <th className="text-left p-2">Tool</th>
                  <th className="text-left p-2">PHQ-9</th>
                  <th className="text-left p-2">GAD-7</th>
                  <th className="text-left p-2">AUDIT</th>
                  <th className="text-left p-2">DAST</th>
                  <th className="text-left p-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {outcomeData.map((row, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{row.clientId}</td>
                    <td className="p-2">{row.therapistName}</td>
                    <td className="p-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {row.formName}
                      </span>
                    </td>
                    <td className="p-2">{row.score_PHQ_9}</td>
                    <td className="p-2">{row.score_GAD_7}</td>
                    <td className="p-2">{row.score_AUDIT}</td>
                    <td className="p-2">{row.score_DAST}</td>
                    <td className="p-2">{new Date(row.submissionDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function ProviderProductivityReport() {
    // Use real data from API and filter out providers with 0 or fewer sessions
    const realProviders = (realData.providers?.data || [])
      .filter(provider => provider.sessions > 0)
      .sort((a, b) => b.sessions - a.sessions); // Sort by sessions descending
    
    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{realProviders.length}</div>
            <div className="text-sm text-blue-600">Active Providers</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">
              {realProviders.reduce((sum, p) => sum + p.sessions, 0)}
            </div>
            <div className="text-sm text-green-600">Total Sessions</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">
              {realProviders.length > 0 ? Math.round(realProviders.reduce((sum, p) => sum + p.sessions, 0) / realProviders.length) : 0}
            </div>
            <div className="text-sm text-yellow-600">Avg Sessions/Provider</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">
              {realProviders.length > 0 ? Math.round(realProviders.reduce((sum, p) => sum + p.completedSessions, 0) / realProviders.reduce((sum, p) => sum + p.sessions, 0) * 100) : 0}%
            </div>
            <div className="text-sm text-purple-600">Overall Completion Rate</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Session Volume by Provider (Active Providers Only)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={realProviders}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="sessions" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Provider Performance Metrics (Filtered: Sessions > 0)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Provider</th>
                  <th className="text-left p-2">License</th>
                  <th className="text-left p-2">Total Clients</th>
                  <th className="text-left p-2">Total Sessions</th>
                  <th className="text-left p-2">Completed Sessions</th>
                  <th className="text-left p-2">Completion Rate</th>
                  <th className="text-left p-2">Avg Weekly</th>
                  <th className="text-left p-2">No-Show Rate</th>
                  <th className="text-left p-2">Avg Improvement</th>
                </tr>
              </thead>
              <tbody>
                {realProviders.map((provider, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{provider.name}</td>
                    <td className="p-2">
                      <span className={provider.license ? 'text-green-600' : 'text-gray-400'}>
                        {provider.license || 'No License'}
                      </span>
                    </td>
                    <td className="p-2 text-center">{provider.clients || 0}</td>
                    <td className="p-2 text-center font-semibold">{provider.sessions}</td>
                    <td className="p-2 text-center text-green-600">{provider.completedSessions}</td>
                    <td className="p-2 text-center">
                      <span className={
                        provider.sessions > 0 
                          ? Math.round((provider.completedSessions / provider.sessions) * 100) > 80 
                            ? 'text-green-600 font-semibold' 
                            : Math.round((provider.completedSessions / provider.sessions) * 100) > 60
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          : 'text-gray-400'
                      }>
                        {provider.sessions > 0 ? Math.round((provider.completedSessions / provider.sessions) * 100) : 0}%
                      </span>
                    </td>
                    <td className="p-2 text-center">{provider.avgWeekly || 0}</td>
                    <td className="p-2 text-center">
                      <span className={provider.noShowRate > 10 ? 'text-red-600' : 'text-green-600'}>
                        {provider.noShowRate || 0}%
                      </span>
                    </td>
                    <td className="p-2 text-center text-blue-600">{provider.avgImprovement || 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function DemographicReport() {
    // Use real data from API
    const realDemographics = realData.demographics?.data || { age: [], gender: [], race: [] };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Age Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={realDemographics.age}
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percentage}) => `${name}: ${percentage}%`}
                >
                  {realDemographics.age.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Race/Ethnicity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={realDemographics.race}
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percentage}) => `${percentage}%`}
                >
                  {realDemographics.race.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Gender Identity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={realDemographics.gender}
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percentage}) => `${percentage}%`}
                >
                  {realDemographics.gender.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Age Breakdown</h3>
            {realDemographics.age.map((item, idx) => (
              <div key={idx} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{item.value} ({item.percentage}%)</span>
              </div>
            ))}
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Race/Ethnicity Breakdown</h3>
            {realDemographics.race.map((item, idx) => (
              <div key={idx} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{item.value} ({item.percentage}%)</span>
              </div>
            ))}
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Gender Breakdown</h3>
            {realDemographics.gender.map((item, idx) => (
              <div key={idx} className="flex justify-between py-1">
                <span>{item.name}</span>
                <span>{item.value} ({item.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function FundingReport() {
    // Use real data from API
    const realFunding = realData.funding?.data || [];
    const realClaims = realData.claims?.data || [];

    // Use chart filter from main component state
    const chartFilter = fundingChartFilter;

    // Use real contract prices and total payments from backend
    console.log('Raw funding data:', realFunding);
    const fundingWithCorrectCosts = realFunding.map(item => {
      console.log('Processing item:', item);
      return {
        ...item,
        avgCost: item.contractPrice || 0, // Use actual contract price from backend
        totalRevenue: item.totalPayment || 0 // Use calculated total payment from backend
      };
    });
    console.log('Processed funding data:', fundingWithCorrectCosts);

    // Transform funding data for chart (showing total payments instead of sessions)
    const fundingChartData = fundingWithCorrectCosts.map(item => ({
      name: item.source || 'Unknown',
      value: item.totalRevenue, // Show total payment instead of sessions
      sessions: item.sessions,
      clients: item.clients,
      avgCost: item.avgCost
    }));

    // Calculate total revenue for percentage calculation
    const totalRevenue = fundingChartData.reduce((sum, item) => sum + item.value, 0);
    const totalSessions = fundingChartData.reduce((sum, item) => sum + item.sessions, 0);
    
    // Filter data for chart based on selection
    let chartData = [];
    let otherData = { name: 'Others', value: 0, clients: 0, avgCost: 0 };
    
    switch (chartFilter) {
      case 'top5':
        chartData = fundingChartData.slice(0, 5);
        otherData.value = fundingChartData.slice(5).reduce((sum, item) => sum + item.value, 0);
        otherData.clients = fundingChartData.slice(5).reduce((sum, item) => sum + item.clients, 0);
        break;
      case 'top10':
        chartData = fundingChartData.slice(0, 10);
        otherData.value = fundingChartData.slice(10).reduce((sum, item) => sum + item.value, 0);
        otherData.clients = fundingChartData.slice(10).reduce((sum, item) => sum + item.clients, 0);
        break;
      case 'medicaid':
        chartData = fundingChartData.filter(item => item.name.toLowerCase().includes('medicaid'));
        break;
      case 'insurance':
        chartData = fundingChartData.filter(item => 
          !item.name.toLowerCase().includes('self-pay') && 
          item.name.toLowerCase() !== 'unknown'
        );
        break;
      case 'all':
      default:
        chartData = fundingChartData;
        break;
    }
    
    // Add "Others" category if we're showing top N
    if ((chartFilter === 'top5' || chartFilter === 'top10') && otherData.value > 0) {
      chartData.push(otherData);
    }

    const fundingWithPercentages = chartData.map(item => ({
      ...item,
      percentage: totalRevenue > 0 ? Math.round((item.value / totalRevenue) * 100) : 0
    }));

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{realFunding.length}</div>
            <div className="text-sm text-blue-600">Funding Sources</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">
              ${totalRevenue.toLocaleString()}
            </div>
            <div className="text-sm text-green-600">Total Revenue</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-2xl font-bold text-yellow-700">
              {totalSessions.toLocaleString()}
            </div>
            <div className="text-sm text-yellow-600">Total Sessions</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">
              ${Math.round(totalRevenue / totalSessions || 0)}
            </div>
            <div className="text-sm text-purple-600">Avg Per Session</div>
          </div>
        </div>

        {/* Key Insights */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-semibold text-blue-700">Top Funding Source</div>
              <div className="text-gray-700">
                {fundingChartData[0]?.name} (${fundingChartData[0]?.value.toLocaleString()} revenue)
              </div>
            </div>
            <div>
              <div className="font-semibold text-green-700">Medicaid Coverage</div>
              <div className="text-gray-700">
                {Math.round((fundingChartData.filter(item => item.name.toLowerCase().includes('medicaid')).reduce((sum, item) => sum + item.value, 0) / totalRevenue) * 100)}% of total revenue
              </div>
            </div>
            <div>
              <div className="font-semibold text-orange-700">Revenue Mix</div>
              <div className="text-gray-700">
                {fundingChartData.filter(item => item.avgCost > 0).length} revenue-generating sources
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Funding Sources Distribution</h3>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">View:</label>
              <select 
                value={chartFilter} 
                onChange={(e) => setFundingChartFilter(e.target.value)}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="top5">Top 5 Sources</option>
                <option value="top10">Top 10 Sources</option>
                <option value="medicaid">Medicaid Only</option>
                <option value="insurance">Insurance Only</option>
                <option value="all">All Sources</option>
              </select>
            </div>
          </div>
          
          {chartFilter === 'all' ? (
            <div className="text-sm text-amber-600 mb-4 p-3 bg-amber-50 rounded">
              ⚠️ Showing all {fundingChartData.length} funding sources. Chart may be crowded. Consider using a filter above.
            </div>
          ) : null}
          
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={fundingWithPercentages} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 11 }}
              />
              <YAxis 
                label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value, name) => [
                  `$${value.toLocaleString()} (${Math.round((value / totalRevenue) * 100)}%)`,
                  'Revenue'
                ]}
                labelFormatter={(label) => `${label}`}
                contentStyle={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6',
                  borderRadius: '4px'
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {fundingWithPercentages.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={
                    entry.name === 'Self-Pay' ? '#FF8042' :
                    entry.name.toLowerCase().includes('medicaid') ? '#00C49F' :
                    entry.name === 'Others' ? '#8884D8' :
                    '#0088FE'
                  } />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Funding Sources Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Funding Source</th>
                  <th className="text-left p-2">Clients</th>
                  <th className="text-left p-2">Sessions</th>
                  <th className="text-left p-2">Contract Price</th>
                  <th className="text-left p-2">Total Payment</th>
                  <th className="text-left p-2">% of Revenue</th>
                </tr>
              </thead>
              <tbody>
                {fundingWithCorrectCosts.map((source, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">
                      <span className={
                        source.source === 'Self-Pay' ? 'text-orange-600' : 
                        source.source?.toLowerCase().includes('medicaid') ? 'text-green-600' :
                        'text-blue-600'
                      }>
                        {source.source || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-2 text-center">{source.clients}</td>
                    <td className="p-2 text-center font-semibold">{source.sessions.toLocaleString()}</td>
                    <td className="p-2 text-center">
                      <span className={
                        source.source === 'Self-Pay' ? 'text-orange-600 font-semibold' :
                        source.source?.toLowerCase().includes('medicaid') ? 'text-green-600 font-semibold' :
                        'text-blue-600 font-semibold'
                      }>
                        ${source.avgCost}
                      </span>
                    </td>
                    <td className="p-2 text-center font-semibold text-green-600">
                      ${source.totalRevenue.toLocaleString()}
                    </td>
                    <td className="p-2 text-center">
                      <span className="font-semibold">
                        {totalRevenue > 0 ? Math.round((source.totalRevenue / totalRevenue) * 100) : 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {realClaims.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Claims Status Analysis</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Count</th>
                    <th className="text-left p-2">Total Charge</th>
                    <th className="text-left p-2">Total Paid</th>
                    <th className="text-left p-2">Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {realClaims.map((claim, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">
                        <span className={
                          claim.status === 'SUCCESS' ? 'text-green-600' : 
                          claim.status === 'PENDING' ? 'text-yellow-600' : 
                          'text-red-600'
                        }>
                          {claim.status}
                        </span>
                      </td>
                      <td className="p-2 text-center">{claim.count}</td>
                      <td className="p-2 text-center">${claim.totalCharge?.toLocaleString()}</td>
                      <td className="p-2 text-center text-green-600">${claim.totalPaid?.toLocaleString()}</td>
                      <td className="p-2 text-center">{claim.successRate?.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  function EngagementReport() {
    // Use real data from API
    const engagementMetrics = realData.engagement?.data?.metrics || [];
    const engagementRates = realData.engagement?.data?.rates || {};
    const engagementSummary = realData.engagement?.data?.summary || {};


    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Attendance Rate</p>
                <p className="text-2xl font-bold text-green-600">{engagementRates.attendanceRate}%</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">No-Show Rate</p>
                <p className="text-2xl font-bold text-red-600">{engagementRates.noShowRate}%</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Users className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clients</p>
                <p className="text-2xl font-bold text-blue-600">{engagementSummary.totalClients}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Engagement Metrics</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={engagementMetrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="metric" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Session Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3">Session Statistics</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Sessions:</span>
                  <span className="font-medium">{engagementSummary.totalSessions?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Average Duration:</span>
                  <span className="font-medium">{engagementSummary.avgSessionDuration} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Completion Rate:</span>
                  <span className="font-medium text-green-600">{engagementRates.completionRate}%</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Engagement Breakdown</h4>
              <div className="space-y-2">
                {engagementMetrics.map((metric, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-gray-600">{metric.metric}:</span>
                    <span className="font-medium">{
                      typeof metric.value === 'number' && metric.value > 100 
                        ? metric.value.toLocaleString() 
                        : metric.value
                    }</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function CustomReportBuilder() {
    const [reportType, setReportType] = useState('clinical');
    const [generatedReport, setGeneratedReport] = useState(null);

    const generateCustomReport = () => {
      const reportData = realData[reportType]?.data || [];
      
      switch (reportType) {
        case 'clinical':
          setGeneratedReport(reportData.map(record => {
            return record;
          }));
          break;
        case 'providers':
          setGeneratedReport(reportData.map(record => {
            return record;
          }));
          break;
        case 'demographics':
          setGeneratedReport(reportData);
          break;
        default:
          setGeneratedReport([]);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Custom Report Builder</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Report Type</label>
              <select 
                value={reportType} 
                onChange={(e) => setReportType(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              >
                <option value="clinical">Clinical Outcomes</option>
                <option value="providers">Provider Performance</option>
                <option value="demographics">Demographics</option>
                <option value="funding">Funding Analysis</option>
                <option value="engagement">Engagement Metrics</option>
              </select>
            </div>
            
            <button 
              onClick={generateCustomReport}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Generate Report
            </button>
          </div>
        </div>

        {generatedReport && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-semibold mb-4">Generated Report</h4>
            <div className="overflow-x-auto">
              <pre className="text-sm bg-gray-50 p-4 rounded">
                {JSON.stringify(generatedReport, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || (() => <div>Component not found</div>);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Clinical Director Dashboard</h1>
          <p className="text-gray-600">Comprehensive analytics and insights for clinical operations</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <FilterControls />

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading data...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="text-red-800 font-medium">Error loading data</div>
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        )}
        
        {!loading && !error && <ActiveComponent />}
      </div>
    </div>
  );
};

export default Dashboard;